import i18n from '@/configuration/i18n/i18n.configuration';
import { nextTick } from 'vue';

/**
 * Gets the current locale based on the i18n language setting.
 * Converts the i18n language code to a full locale format (e.g., 'es' → 'es-ES', 'fr' → 'fr-FR').
 *
 * @returns The formatted locale string according to current i18n language.
 */
function getLocale(): string {
  const currentLanguage = i18n.global.locale.value;
  return currentLanguage.toLowerCase() + '-' + currentLanguage.toUpperCase();
}

/**
 * Scrolls the given element to the bottom with a smooth animation.
 * @param {HTMLElement | null} element - The element to scroll.
 * @param {boolean} [instant=false] - If true, scrolls instantly without animation.
 */
function scrollToElementsBottom(
  element: HTMLElement | null,
  instant: boolean = false
): void {
  nextTick(() => {
    if (!element) return;
    element.scroll({
      top: element.scrollHeight,
      behavior: instant ? 'auto' : 'smooth'
    });
  });
}

/**
 * Gets the current time formatted as HH:MM:SS.
 * @returns {string} The current time.
 */
function getCurrentTime(existingDate: string | null = null): string {
  const date = existingDate ? new Date(existingDate) : new Date();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
}

/**
 * Formats a given date as a string using the specified formatting options and the current i18n locale.
 *
 * @param date - The date to format, either as a `Date` object or an ISO date string.
 * @param options - Optional. Formatting options for the output string, following `Intl.DateTimeFormatOptions`.
 *                  Defaults to `{ year: 'numeric', month: 'short', day: '2-digit' }`.
 * @returns The formatted date string according to the current locale and provided options.
 */
function getFormatedDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getLocale(), options).format(dateObj);
}

/**
 * Formats a number with thousands separator according to the current i18n locale.
 * Converts the i18n language code to a full locale format (e.g., 'es' → 'es-ES', 'fr' → 'fr-FR').
 *
 * @param number - The number to format.
 * @returns The formatted number string with thousands separator according to current locale.
 */
function formatNumber(number: number): string {
  return new Intl.NumberFormat(getLocale()).format(number);
}

/**
 * Calculates the number of full days between two dates.
 *
 * @param startDate - The start date.
 * @param endDate - The end date.
 * @returns The number of full days between `startDate` and `endDate`.
 */
function daysBetweenDates(startDate: Date, endDate: Date): number {
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  return Math.floor((endDate.getTime() - startDate.getTime()) / oneDay);
}

/**
 * Removes all occurrences of the specified HTML tags from the input string.
 *
 * @param input - The string from which to remove the tags.
 * @param tags - An array of tag names (without angle brackets) to be removed from the input.
 * @returns The input string with the specified tags removed.
 */
function removeTags(
  input: string,
  tags: string[],
  removeCommas?: boolean
): string {
  for (const tag of tags) {
    // If removeCommas is true, replace commas inside the tag content with an empty string
    if (removeCommas) {
      const commaRegex = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'g');
      input = input.replace(commaRegex, (match) => {
        return match.replace(/,/g, '');
      });
    }

    input = input.replaceAll(`<${tag}>`, '');
    input = input.replaceAll(`</${tag}>`, '');
  }

  return input;
}

/**
 * Removes all occurrences of specified HTML-like tags and their content from the input string.
 *
 * @param input - The string from which to remove the tags and their content.
 * @param tags - An array of tag names (without angle brackets) to be removed from the input.
 * @returns The input string with the specified tags and their enclosed content removed.
 */
function removeTagsAndContent(input: string, tags: string[]): string {
  for (const tag of tags) {
    const regex = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'g');
    input = input.replace(regex, '');
  }

  return input;
}

/**
 * Replaces all occurrences of specified substrings in the input text with their corresponding replacement values.
 *
 * Iterates over the `replacements` object, where each key is treated as a regular expression pattern (without escaping),
 * and replaces all matches in the `text` with the associated value.
 *
 * @param text - The input string in which replacements will be made.
 * @param replacements - An object mapping substrings (as regex patterns) to their replacement strings.
 * @returns The resulting string after all replacements have been applied.
 */
function replaceInText(
  text: string,
  replacements: Record<string, string>
): string {
  let result = text;
  for (const [key, value] of Object.entries(replacements)) {
    const regex = new RegExp(key, 'g');
    result = result.replace(regex, value);
  }
  return result;
}

/**
 * Removes trailing <referenceDoc> tags and their content from the input string.
 *
 * @param text - The string to sanitize.
 * @returns The sanitized string with trailing <referenceDoc> content removed.
 */
function santizeReferences(text: string): string {
  return text.replace(/<referenceDoc>[^<]*$/, '');
}

export {
  daysBetweenDates,
  formatNumber,
  getCurrentTime,
  getFormatedDate,
  removeTags,
  removeTagsAndContent,
  replaceInText,
  scrollToElementsBottom,
  santizeReferences
};
